import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import DualFooterButton from '../Common/Button/DualFooterButton';
import RadioList, { OptionType } from '../Common/Input/Radio/RadioList';
import TextInputLabelUp from '../Common/Input/TextInputLabelUp';
import BottomSheetPopup from '../Common/Popup/BottomSheetPopup';
import { ChevronDownIcon } from '../Icon';
import NoAxisWarning from '../Products/NoAxisWarning';
import { getHorsepower } from '@/api/public';
import { colors } from '@/const/colors';
import {
  DISTANCE_RANGE_ERROR_MESSAGE,
} from '@/const/errorMessage';
import { positiveFloatRegex } from '@/const/regex';
import { usePriceSearchFormContext } from '@/contexts/Price/PriceSearchFormContext';
import { useProductEnumContext } from '@/contexts/Products/ProductEnumContext';
import { formatValueToDecimalPlaces, getCurrentYear, getFormatDateOnlyNum, isUnderFourTons } from '@/utils/common';
import {
  getDistanceErrorMessage,
  getLoadedInnerAreaErrorMessage,
  getLoadedInnerHeightErrorMessage,
  getTonnageErrorMessage,
  getYearErrorMessage,
  validateLoadedInnerLength,
  processDistanceInput,
  processTonnageInput,
  processYearInput,
} from '@/utils/validation';

interface ProductProps {
  ownerInfo?: OwnerInfo;
  productOriginData?: ProductDetailResponse | undefined;
  productSearchParams: ProductSearchParams;
  setProductSearchParams: React.Dispatch<React.SetStateAction<ProductSearchParams>>;
}

const PriceTrendSearchForm: React.FC<ProductProps> = ({
  ownerInfo,
  productOriginData,
  productSearchParams,
  setProductSearchParams,
}) => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const [optionData, setOptionData] = useState<OptionType[]>([]);
  const { priceProductSearchParams, setPriceProductSearchParams } = usePriceSearchFormContext();
  const { productEnum } = useProductEnumContext();
  const yearRef = useRef<HTMLInputElement>(null);
  const tonsRef = useRef<HTMLInputElement>(null);
  const loadedInnerLengthRef = useRef<HTMLInputElement>(null);
  const loadedInnerAreaRef = useRef<HTMLInputElement>(null);
  const loadedInnerHeightRef = useRef<HTMLInputElement>(null);
  const distanceRef = useRef<HTMLInputElement>(null);

  const [yearError, setYearError] = useState(false);
  const [tonsError, setTonsError] = useState(false);
  const [loadedInnerLengthError, setLoadedInnerLengthError] = useState(false);
  const [loadedInnerAreaError, setLoadedInnerAreaError] = useState(false);
  const [loadedInnerHeightError, setLoadedInnerHeightError] = useState(false);
  const [distanceError, setDistanceError] = useState(false);
  const [yearErrorMsg, setYearErrorMsg] = useState('');
  const [tonsErrorMsg, setTonsErrorMsg] = useState('');
  const [loadedInnerLengthErrorMsg, setLoadedInnerLengthErrorMsg] = useState('');
  const [loadedInnerAreaErrorMsg, setLoadedInnerAreaErrorMsg] = useState('');
  const [loadedInnerHeightErrorMsg, setLoadedInnerHeightErrorMsg] = useState('');
  const [distanceErrorMsg, setDistanceErrorMsg] = useState('');
  const [powerError, setPowerError] = useState(false);
  const [powerErrorMsg, setPowerErrorMsg] = useState('');
  const [horsepowerOptions, setHorsepowerOptions] = useState<OptionType[]>([]);
  const [isLoadingHorsepower, setIsLoadingHorsepower] = useState(false);
  const hasValidatedPower = useRef(false);
  const lastValidatedKey = useRef<string>('');

  const [isShow, setIsShow] = useState(false);
  const [title, setTitle] = useState('');

  const keyValueList: KeyValueListType = {
    manufacturerCategories: '제조사',
    model: '모델',
    loaded: '적재함 종류',
    fuel: '연료',
    power: '마력수',
  };

  const [axisOptionData, setAxisOptionData] = useState<OptionType[]>([]);

  useEffect(() => {
    if (productOriginData) {
      // transmission desc 매핑
      let transmissionDesc = productOriginData?.transmission?.desc || '';
      if (productOriginData?.transmission?.code === 'AUTO') {
        transmissionDesc = '오토';
      } else if (
        productOriginData?.transmission?.code === 'STICK' ||
        productOriginData?.transmission?.code === 'MANUAL'
      ) {
        transmissionDesc = '스틱';
      }

      // fuel desc 매핑
      let fuelDesc = productOriginData?.fuel?.desc || '';
      if (productOriginData?.fuel?.code === 'DIESEL') {
        fuelDesc = '디젤';
      } else if (productOriginData?.fuel?.code === 'LPG') {
        fuelDesc = 'LPG';
      } else if (productOriginData?.fuel?.code === 'ELECTRIC' || productOriginData?.fuel?.code === 'ELECT') {
        fuelDesc = '전기';
      } else if (productOriginData?.fuel?.code === 'GASOLINE' || productOriginData?.fuel?.code === 'GAS') {
        fuelDesc = '가솔린';
      }

      setProductSearchParams({
        ...productSearchParams,
        year: getFormatDateOnlyNum(productOriginData?.firstRegistrationDate, ['year']),
        manufacturerCategories: productOriginData?.manufacturerCategories,
        model: productOriginData?.model,
        tons: productOriginData?.tons,
        loaded: productOriginData?.loaded,
        loadedInnerLength: productOriginData?.loadedInnerLength || '',
        loadedInnerArea: productOriginData?.loadedInnerArea || '',
        loadedInnerHeight: productOriginData?.loadedInnerHeight || '',
        transmission: transmissionDesc,
        distance: productOriginData?.distance ? productOriginData.distance.toString() : '',
        fuel: fuelDesc,
        power: productOriginData?.power || '',
        axis: isUnderFourTons(productOriginData?.tons) ? { code: 'NONE', desc: '없음' } : productOriginData?.axis,
        productId: productOriginData?.id || null,
        status: productOriginData?.status,
      });
    }
  }, [productOriginData]);

  const onClickSelectBox = (key: string) => {
    if (!productEnum) return;
    const arr: OptionDataType[] = [];
    setTitle(key);

    if (key === 'manufacturerCategories') {
      const arr = productEnum['manufacturerAndModel'].map((item) => item.manufacturerCategories);
      if (arr.length > 0) {
        setOptionData(
          arr.map(
            (item) =>
              ({
                id: item?.id,
                code: item?.code,
                desc: item?.name,
              } as OptionType),
          ),
        );
      }
    } else if (key === 'model') {
      if (productOriginData?.manufacturerCategories || productSearchParams.manufacturerCategories) {
        const arr = productOriginData?.manufacturerCategories
          ? productEnum['manufacturerAndModel']
              .filter((item) => item.manufacturerCategories.id === productOriginData?.manufacturerCategories?.id)
              .map((item) => item.model)
          : productEnum['manufacturerAndModel']
              .filter((item) => item.manufacturerCategories.id === productSearchParams?.manufacturerCategories?.id)
              .map((item) => item.model);

        if (arr.length > 0) {
          const flattenedArr = arr.reduce<Model[]>((acc, val) => acc.concat(val), []);
          setOptionData(
            flattenedArr.map((item) => ({
              id: item?.id,
              code: String(item.id),
              desc: item?.name,
            })),
          );
        }
      }
    } else if (key === 'fuel') {
      // fuel의 desc를 한국어로 매핑
      const fuelData = (productEnum[key] as FilterDataType[]).map((item) => {
        let desc = item.desc;
        if (item.code === 'DIESEL') {
          desc = '디젤';
        } else if (item.code === 'LPG') {
          desc = 'LPG';
        } else if (item.code === 'ELECTRIC' || item.code === 'ELECT') {
          desc = '전기';
        } else if (item.code === 'GASOLINE' || item.code === 'GAS') {
          desc = '가솔린';
        }
        return {
          ...item,
          desc: desc,
        };
      });
      setOptionData(fuelData);
    } else if (key === 'loaded') {
      const loadedData = (productEnum[key] as FilterDataType[]).filter((item) => item.code !== 'WIDEWINGBODY');
      setOptionData(loadedData);
    } else {
      setOptionData(productEnum[key] as FilterDataType[]);
    }
    setIsShow(true);
  };

  const onClickItem = (item: OptionDataType, key: string) => {
    let { code, id, desc } = item;
    switch (key) {
      case 'manufacturerCategories':
        setProductSearchParams({
          ...productSearchParams,
          manufacturerCategories: { code, id, name: desc } as ManufacturerCategories,
        });
        setTimeout(() => {
          setIsShow(false);
        }, 200);
        break;

      case 'model':
        setProductSearchParams({
          ...productSearchParams,
          model: { id, name: desc } as Model,
        });
        setTimeout(() => {
          setIsShow(false);
        }, 200);

        break;

      case 'loaded':
        setProductSearchParams({
          ...productSearchParams,
          loaded: item as EnumPresenter,
        });
        setTimeout(() => {
          setIsShow(false);
        }, 200);
        break;

      case 'fuel':
        // fuel의 desc를 한국어로 매핑
        let fuelDesc = desc;
        if (code === 'DIESEL') {
          fuelDesc = '디젤';
        } else if (code === 'LPG') {
          fuelDesc = 'LPG';
        } else if (code === 'ELECTRIC' || code === 'ELECT') {
          fuelDesc = '전기';
        } else if (code === 'GASOLINE' || code === 'GAS') {
          fuelDesc = '가솔린';
        }
        setProductSearchParams({
          ...productSearchParams,
          fuel: fuelDesc,
        });
        setTimeout(() => {
          setIsShow(false);
        }, 200);
        break;
    }
  };

  const handleChangeYearInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = processYearInput(value);
    setProductSearchParams({ ...productSearchParams, [name]: trimmedValue });

    const errorMessage = getYearErrorMessage(trimmedValue);

    if (errorMessage) {
      setYearError(true);
      setYearErrorMsg(errorMessage);
    } else {
      setYearError(false);
      setYearErrorMsg('');
    }
  };

  const handleChangeTonsInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const trimmedValue = processTonnageInput(value, 1);

    setProductSearchParams({ ...productSearchParams, [name]: trimmedValue });
    const errorMessage = getTonnageErrorMessage(trimmedValue);

    if (errorMessage) {
      setTonsError(true);
      setTonsErrorMsg(errorMessage);
    } else {
      setTonsError(false);
      setTonsErrorMsg('');
    }
  };

  const handleChangeLoadedLengthInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const decimalPlaces = 2;
    const formattedValue = formatValueToDecimalPlaces(value, decimalPlaces);

    const isInvalidInput = formattedValue.startsWith('0') && formattedValue.length > 1;

    if (isInvalidInput) {
      setProductSearchParams({ ...productSearchParams, [name]: '0' });
      return;
    }

    if (!formattedValue) {
      setLoadedInnerLengthError(false);
      setLoadedInnerLengthErrorMsg('');
    } else {
      const { hasError, errorMsg } = validateLoadedInnerLength(
        Number(formattedValue),
        Number(productSearchParams?.tons),
        productSearchParams?.manufacturerCategories?.code ?? '',
        productSearchParams?.model?.id ?? 0,
      );
      setLoadedInnerLengthError(hasError);
      setLoadedInnerLengthErrorMsg(errorMsg);
    }

    if (positiveFloatRegex.test(value.trim()) || value.trim() === '') {
      const str = formattedValue.trim() === '' ? '' : formattedValue.trim();
      setProductSearchParams({ ...productSearchParams, [name]: str });
    }
  };

  const handleChangeLoadedAreaInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const decimalPlaces = 2;
    const formattedValue = formatValueToDecimalPlaces(value, decimalPlaces);

    const isInvalidInput = formattedValue.startsWith('0') && formattedValue.length > 1;

    if (isInvalidInput) {
      setProductSearchParams({ ...productSearchParams, [name]: '0' });
      return;
    }

    const errorMessage = getLoadedInnerAreaErrorMessage(formattedValue);
    if (errorMessage) {
      setLoadedInnerAreaError(true);
      setLoadedInnerAreaErrorMsg(errorMessage);
    } else {
      setLoadedInnerAreaError(false);
      setLoadedInnerAreaErrorMsg('');
    }

    if (positiveFloatRegex.test(value.trim()) || value.trim() === '') {
      const str = formattedValue.trim() === '' ? '' : formattedValue.trim();
      setProductSearchParams({ ...productSearchParams, [name]: str });
    }
  };

  const handleChangeLoadedHeightInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const decimalPlaces = 2;
    const formattedValue = formatValueToDecimalPlaces(value, decimalPlaces);

    const isInvalidInput = formattedValue.startsWith('0') && formattedValue.length > 1;

    if (isInvalidInput) {
      setProductSearchParams({ ...productSearchParams, [name]: '0' });
      return;
    }

    const errorMessage = getLoadedInnerHeightErrorMessage(formattedValue);
    if (errorMessage) {
      setLoadedInnerHeightError(true);
      setLoadedInnerHeightErrorMsg(errorMessage);
    } else {
      setLoadedInnerHeightError(false);
      setLoadedInnerHeightErrorMsg('');
    }

    if (positiveFloatRegex.test(value.trim()) || value.trim() === '') {
      const str = formattedValue.trim() === '' ? '' : formattedValue.trim();
      setProductSearchParams({ ...productSearchParams, [name]: str });
    }
  };

  const handleChangeDistanceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = processDistanceInput(value);
    setProductSearchParams({ ...productSearchParams, [name]: trimmedValue });

    const errorMessage = getDistanceErrorMessage(trimmedValue);
    if (errorMessage) {
      setDistanceError(true);
      setDistanceErrorMsg(errorMessage);
    } else {
      setDistanceError(false);
      setDistanceErrorMsg('');
    }
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let { value } = e.target;
    const valueNum = Number(value);
    if (valueNum >= 100) {
      setProductSearchParams({
        ...productSearchParams,
        loadedInnerLength: parseFloat((valueNum / 1000).toFixed(2)).toString(),
      });
    }
  };

  const onChangeRadioInput = (newVal: OptionType, key: string) => {
    if (key === 'transmission') {
      // desc를 한국어로 매핑해서 저장
      let desc = '오토';
      if (newVal.code === 'AUTO') {
        desc = '오토';
      } else if (newVal.code === 'STICK' || newVal.code === 'MANUAL') {
        desc = '스틱';
      }
      setProductSearchParams({ ...productSearchParams, [key]: desc });
    } else {
      setProductSearchParams({ ...productSearchParams, [key]: newVal });
    }
  };

  useEffect(() => {
    if (productEnum) {
      setAxisOptionData(productEnum.axis);
    }
  }, [productEnum]);

  const getValue = (title: string) => {
    if (title === 'manufacturerCategories') {
      return productSearchParams.manufacturerCategories?.code;
    } else if (title === 'model') {
      return String(productSearchParams.model?.id);
    } else if (title === 'fuel') {
      // fuel은 desc로 저장되어 있으므로, desc로 매칭되는 code를 찾아야 함
      const fuelValue =
        typeof productSearchParams.fuel === 'string'
          ? productSearchParams.fuel
          : productSearchParams.fuel && typeof productSearchParams.fuel === 'object'
          ? (productSearchParams.fuel as unknown as EnumPresenter).desc || ''
          : '';
      const fuelItem = productEnum?.fuel?.find((item) => {
        let desc = item.desc;
        if (item.code === 'DIESEL') desc = '디젤';
        else if (item.code === 'LPG') desc = 'LPG';
        else if (item.code === 'ELECTRIC' || item.code === 'ELECT') desc = '전기';
        else if (item.code === 'GASOLINE' || item.code === 'GAS') desc = '가솔린';
        return desc === fuelValue;
      });
      return fuelItem?.code || '';
    } else if (title === 'transmission') {
      // transmission은 desc로 저장되어 있으므로, desc로 매칭되는 code를 찾아야 함
      const transmissionValue =
        typeof productSearchParams.transmission === 'string'
          ? productSearchParams.transmission
          : productSearchParams.transmission && typeof productSearchParams.transmission === 'object'
          ? (productSearchParams.transmission as unknown as EnumPresenter).desc || ''
          : '';
      const transmissionItem = productEnum?.transmission?.find((item) => {
        let desc = item.desc;
        if (item.code === 'AUTO') desc = '오토';
        else if (item.code === 'STICK' || item.code === 'MANUAL') desc = '스틱';
        return desc === transmissionValue;
      });
      return transmissionItem?.code || '';
    } else {
      return productSearchParams.loaded?.code;
    }
  };

  const scrollIntoView = (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e) {
      const { name } = e.target;
      if (name === 'year') {
        yearRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'tons') {
        tonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerLength') {
        loadedInnerLengthRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerArea') {
        loadedInnerAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'loadedInnerHeight') {
        loadedInnerHeightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } else if (name === 'distance') {
        distanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  };

  useEffect(() => {
    if (!productSearchParams.loadedInnerLength) {
      setLoadedInnerLengthError(false);
      setLoadedInnerLengthErrorMsg('');
      return;
    }
    const { hasError, errorMsg } = validateLoadedInnerLength(
      parseFloat(productSearchParams.loadedInnerLength),
      Number(productSearchParams?.tons),
      productSearchParams?.manufacturerCategories?.code ?? '',
      productSearchParams?.model?.id ?? 0,
    );
    setLoadedInnerLengthError(hasError);
    setLoadedInnerLengthErrorMsg(errorMsg);
  }, [
    productSearchParams.loadedInnerLength,
    productSearchParams.tons,
    productSearchParams?.manufacturerCategories?.code,
    productSearchParams?.model?.id,
  ]);

  useEffect(() => {
    validatePowerWithApi();
  }, [productSearchParams?.manufacturerCategories?.name, productSearchParams?.model?.name]);

  useEffect(() => {
    const isDisabled =
      !productSearchParams?.manufacturerCategories?.name ||
      !productSearchParams?.model?.name ||
      !productSearchParams?.tons;

    if (isDisabled) {
      setPowerError(false);
      setPowerErrorMsg('');
    }
  }, [productSearchParams?.manufacturerCategories?.name, productSearchParams?.model?.name, productSearchParams?.tons]);

  useLayoutEffect(() => {
    const detectMobileKeyboard = () => {
      scrollIntoView();
    };
    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  });

  const onClickSearch = () => {
    navigate('/price-trend/result', {
      state: { productSearchParams, productOriginData: location.state?.productOriginData },
    });
  };

  const clear = () => {
    setProductSearchParams({
      tons: '',
      year: '',
      model: { id: 0, name: '' },
      manufacturerCategories: { id: 0, name: '', code: '' },
      loaded: { code: '', desc: '' },
      loadedInnerLength: '',
      loadedInnerArea: '',
      loadedInnerHeight: '',
      transmission: '',
      distance: '',
      fuel: '',
      power: '',
      axis: { code: '', desc: '' },
    });

    setYearError(false);
    setYearErrorMsg('');
    setTonsError(false);
    setTonsErrorMsg('');
    setLoadedInnerLengthError(false);
    setLoadedInnerLengthErrorMsg('');
    setLoadedInnerAreaError(false);
    setLoadedInnerAreaErrorMsg('');
    setLoadedInnerHeightError(false);
    setLoadedInnerHeightErrorMsg('');
    setDistanceError(false);
    setDistanceErrorMsg('');
    setPowerError(false);
    setPowerErrorMsg('');
  };

  useEffect(() => {
    if (navigationType === NavigationType.Pop && priceProductSearchParams) {
      // fuel과 transmission이 객체인 경우 문자열로 변환
      const restoredParams = { ...priceProductSearchParams };
      if (restoredParams.fuel && typeof restoredParams.fuel !== 'string' && typeof restoredParams.fuel === 'object') {
        restoredParams.fuel = (restoredParams.fuel as unknown as EnumPresenter).desc || '';
      }
      if (
        restoredParams.transmission &&
        typeof restoredParams.transmission !== 'string' &&
        typeof restoredParams.transmission === 'object'
      ) {
        restoredParams.transmission = (restoredParams.transmission as unknown as EnumPresenter).desc || '';
      }
      setProductSearchParams(restoredParams);
    } else {
      setPriceProductSearchParams({});
    }
  }, []);

  const isDisabledSearchBtn = () => {
    return (
      yearError ||
      tonsError ||
      loadedInnerLengthError ||
      // loadedInnerAreaError와 loadedInnerHeightError는 필수값이 아니므로 버튼 비활성화 조건에서 제외
      // loadedInnerAreaError ||
      // loadedInnerHeightError ||
      distanceError ||
      productSearchParams?.axis === null ||
      productSearchParams?.axis?.code === '' ||
      productSearchParams?.loaded?.code === '' ||
      !productSearchParams?.loadedInnerLength ||
      // !productSearchParams?.loadedInnerArea ||
      // !productSearchParams?.loadedInnerHeight ||
      !productSearchParams?.transmission ||
      !productSearchParams?.distance ||
      !productSearchParams?.fuel ||
      productSearchParams?.model === null ||
      productSearchParams?.model?.name === '' ||
      productSearchParams?.tons === '' ||
      !productSearchParams?.power
    );
  };

  // 마력수 값 검증 및 자동 설정 함수
  const validatePowerWithApi = async () => {
    const manufacturer = productSearchParams?.manufacturerCategories?.name;
    const modelName = productSearchParams?.model?.name;
    const tons = productSearchParams?.tons;
    const currentPower = productSearchParams?.power;

    // 파라미터가 모두 있어야 함
    if (!manufacturer || !modelName || !tons) {
      return;
    }

    // 제조사/모델명/톤수 조합으로 검증 키 생성
    const validationKey = `${manufacturer}-${modelName}-${tons}`;

    // 같은 조합으로 이미 검증했으면 다시 실행하지 않음
    if (hasValidatedPower.current && lastValidatedKey.current === validationKey) {
      return;
    }

    // 검증 키가 변경되면 리셋
    if (lastValidatedKey.current && lastValidatedKey.current !== validationKey) {
      hasValidatedPower.current = false;
    }

    // 검증 시작 표시
    hasValidatedPower.current = true;
    lastValidatedKey.current = validationKey;

    try {
      const response = await getHorsepower({
        manufacturer,
        modelName,
        tons: String(tons),
        horsePower: String(currentPower || ''),
      });

      if (response?.result === 'Y' && response?.horsepower && Array.isArray(response.horsepower)) {
        if (response.horsepower.length === 0) {
          // API 응답이 빈 배열인 경우 0으로 자동 설정
          setProductSearchParams((prev) => ({ ...prev, power: '0' }));
          setPowerError(false);
          setPowerErrorMsg('');
        } else {
          // API 응답에 값이 있는 경우
          const powerList = response.horsepower.map((hp: number) => String(hp));
          if (currentPower && powerList.includes(currentPower)) {
            // 현재 값이 API 응답에 포함되어 있으면 유지
            setPowerError(false);
            setPowerErrorMsg('');
          } else {
            // 현재 값이 없거나 API 응답에 없으면 초기화
            setProductSearchParams((prev) => ({ ...prev, power: '' }));
            // setPowerError(true);
            // setPowerErrorMsg('마력을 선택해주세요.');
          }
        }
      } else {
        // API 응답이 없거나 잘못된 경우 0으로 자동 설정
        setProductSearchParams((prev) => ({ ...prev, power: '0' }));
        setPowerError(false);
        setPowerErrorMsg('');
      }
    } catch (error) {
      console.error('마력수 검증 실패:', error);
      // 에러 발생 시 0으로 자동 설정
      setProductSearchParams((prev) => ({ ...prev, power: '0' }));
      setPowerError(false);
      setPowerErrorMsg('');
    }
  };

  const fetchHorsepower = async () => {
    const manufacturer = productSearchParams?.manufacturerCategories?.name;
    const modelName = productSearchParams?.model?.name;
    const tons = productSearchParams?.tons;

    // 파라미터 체크
    if (!manufacturer || !modelName || !tons) {
      return;
    }

    setIsLoadingHorsepower(true);
    try {
      const response = await getHorsepower({
        manufacturer,
        modelName,
        tons: String(tons),
        horsePower: '',
      });

      if (response?.result === 'Y' && response?.horsepower && Array.isArray(response.horsepower)) {
        if (response.horsepower.length === 0) {
          // 빈 배열인 경우 0만 표시
          setHorsepowerOptions([{ code: '0', desc: '0' }]);
        } else {
          const options: OptionType[] = response.horsepower.map((hp: number) => ({
            code: String(hp),
            desc: String(hp),
          }));
          setHorsepowerOptions(options);
        }
        setIsShow(true);
        setTitle('power');
      } else {
        // API에 값이 없으면 0 반환
        setHorsepowerOptions([{ code: '0', desc: '0' }]);
        setIsShow(true);
        setTitle('power');
      }
    } catch (error) {
      console.error('마력수 조회 실패:', error);
      setHorsepowerOptions([{ code: '0', desc: '0' }]);
      setIsShow(true);
      setTitle('power');
    } finally {
      setIsLoadingHorsepower(false);
    }
  };

  const handleClickPowerSelectBox = () => {
    fetchHorsepower();
  };

  const handleBlurTonsInput = () => {
    if (isUnderFourTons(productSearchParams.tons)) {
      setProductSearchParams({
        ...productSearchParams,
        axis: { code: 'NONE', desc: '없음' },
      });
    }
    // 톤수 blur 시 마력수 API 호출 및 검증
    hasValidatedPower.current = false;
    lastValidatedKey.current = '';
    validatePowerWithApi();
  };

  return (
    <>
      <div>
        {productOriginData && (
          <h2 className="pt-6 mt-2 mb-6 text-2xl font-bold text-gray-8">{ownerInfo?.name}님의 차량정보</h2>
        )}
        <div>
          {productOriginData && (
            <div className="inline-block w-auto p-3 border-2 border-black rounded-lg bg-yellow">
              <div className="flex items-center justify-center mb-2 text-3xl font-semibold">
                {productOriginData?.truckNumber}
              </div>
            </div>
          )}

          <div className="rounded-lg bg-blue-0 px-4 py-[19px] mt-4 mb-2 flex flex-col text-gray-8">
            <h4 className="mb-2 text-lg font-bold text-gray-8">
              {productOriginData ? '추가 정보 입력' : '필터로 시세 검색'}
            </h4>
            <p className="text-sm text-gray-8">
              {productOriginData ? '정확한 시세 검색을 위해 추가 ' : '정확한 시세 검색을 위해 '}
              정보를 입력해주세요.
            </p>
          </div>
          <span className="mb-4 text-sm text-red">(필수) 항목은 필수 항목입니다.</span>

          <div className="flex flex-col gap-[30px] pt-[30px]">
            <TextInputLabelUp
              ref={yearRef}
              name="year"
              label="연식"
              placeholder={`연식 입력 ex) ${getCurrentYear()}`}
              value={productSearchParams?.year || ''}
              onChange={(e) => handleChangeYearInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              error={yearError}
              errorMsg={yearErrorMsg}
              inputMode="numeric"
              maxLength={4}
              disabled={!!productSearchParams?.productId}
              required
            ></TextInputLabelUp>

            <div onClick={() => onClickSelectBox('manufacturerCategories')}>
              <TextInputLabelUp
                name="model"
                label="제조사"
                placeholder="제조사 선택"
                value={productSearchParams?.manufacturerCategories?.name || ''}
                type="text"
                readOnly={!productOriginData}
                disabled={!!productOriginData}
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
              ></TextInputLabelUp>
            </div>

            <div onClick={() => onClickSelectBox('model')}>
              <TextInputLabelUp
                name="model"
                label="모델"
                placeholder="모델 선택"
                value={productSearchParams?.model?.name || ''}
                type="text"
                disabled={
                  !productOriginData?.manufacturerCategories?.name && !productSearchParams?.manufacturerCategories?.name
                }
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
              ></TextInputLabelUp>
            </div>

            <TextInputLabelUp
              ref={tonsRef}
              name="tons"
              label="톤수"
              placeholder="톤수 입력 ex) 8.5"
              value={productSearchParams?.tons || ''}
              onChange={(e) => handleChangeTonsInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              onBlur={() => handleBlurTonsInput()}
              error={tonsError}
              errorMsg={tonsErrorMsg}
              suffix="t"
              maxLength={4}
              required
            ></TextInputLabelUp>

            <div onClick={handleClickPowerSelectBox}>
              <TextInputLabelUp
                name="power"
                label="마력수"
                placeholder="마력수 선택"
                value={productSearchParams?.power || ''}
                type="text"
                disabled={
                  !productSearchParams?.manufacturerCategories?.name ||
                  !productSearchParams?.model?.name ||
                  !productSearchParams?.tons
                }
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
                error={powerError}
                errorMsg={powerErrorMsg}
              ></TextInputLabelUp>
            </div>

            <div onClick={() => onClickSelectBox('loaded')}>
              <TextInputLabelUp
                name="loaded"
                label="적재함 종류"
                placeholder="적재함 종류 선택"
                value={productSearchParams?.loaded?.desc || ''}
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
              ></TextInputLabelUp>
            </div>

            <TextInputLabelUp
              ref={loadedInnerLengthRef}
              name="loadedInnerLength"
              label="길이"
              placeholder="길이 입력 ex) 10.2"
              value={String(productSearchParams?.loadedInnerLength || '')}
              onChange={(e) => handleChangeLoadedLengthInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              onBlur={(e) => handleInputBlur(e)}
              error={loadedInnerLengthError}
              errorMsg={loadedInnerLengthErrorMsg}
              type="text"
              suffix="m"
              maxLength={5}
              required
            ></TextInputLabelUp>

            <TextInputLabelUp
              ref={loadedInnerAreaRef}
              name="loadedInnerArea"
              label="너비"
              placeholder="너비 입력 ex) 2.4"
              value={String(productSearchParams?.loadedInnerArea || '')}
              onChange={(e) => handleChangeLoadedAreaInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              error={loadedInnerAreaError}
              errorMsg={loadedInnerAreaErrorMsg}
              type="text"
              suffix="m"
              maxLength={5}
            ></TextInputLabelUp>

            <TextInputLabelUp
              ref={loadedInnerHeightRef}
              name="loadedInnerHeight"
              label="높이"
              placeholder="높이 입력 ex) 2.5"
              value={String(productSearchParams?.loadedInnerHeight || '')}
              onChange={(e) => handleChangeLoadedHeightInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              error={loadedInnerHeightError}
              errorMsg={loadedInnerHeightErrorMsg}
              type="text"
              suffix="m"
              maxLength={5}
            ></TextInputLabelUp>

            <div>
              <p className="mb-2 font-semibold text-gray-8">
                변속기 <span className="text-base font-normal text-red">(필수)</span>
              </p>
              <div className="px-1">
                <RadioList
                  name="transmission"
                  list={
                    (productEnum?.transmission
                      .filter((item) => item.code !== 'BOTH')
                      .map((item) => {
                        let desc = item.desc;
                        if (item.code === 'AUTO') {
                          desc = '오토';
                        } else if (item.code === 'STICK' || item.code === 'MANUAL') {
                          desc = '스틱';
                        }
                        return {
                          id: (item as FilterDataType).id || 0,
                          code: item.code,
                          desc: desc,
                        };
                      }) as OptionType[]) || []
                  }
                  value={getValue('transmission') || ''}
                  onChange={(val) => {
                    onChangeRadioInput(val, 'transmission');
                  }}
                  justify="equal"
                ></RadioList>
              </div>
            </div>

            <TextInputLabelUp
              ref={distanceRef}
              name="distance"
              label="주행거리"
              placeholder="주행거리 입력 ex) 20000"
              value={String(productSearchParams?.distance || '')}
              onChange={(e) => handleChangeDistanceInput(e)}
              onFocus={(e) => scrollIntoView(e)}
              error={distanceError}
              errorMsg={distanceErrorMsg}
              type="text"
              suffix="km"
              maxLength={7}
              inputMode="numeric"
              required
            ></TextInputLabelUp>

            <div onClick={() => onClickSelectBox('fuel')}>
              <TextInputLabelUp
                name="fuel"
                label="연료(필수)"
                placeholder="연료 선택"
                value={
                  typeof productSearchParams?.fuel === 'string'
                    ? productSearchParams.fuel
                    : productSearchParams?.fuel && typeof productSearchParams.fuel === 'object'
                    ? (productSearchParams.fuel as unknown as EnumPresenter).desc || ''
                    : ''
                }
                type="text"
                readOnly
                suffix={<ChevronDownIcon color={colors.gray[8]}></ChevronDownIcon>}
                required
              ></TextInputLabelUp>
            </div>

            <div>
              <p className="mb-2 font-semibold text-gray-8">
                가변축 <span className="text-base font-normal text-red">(필수)</span>
              </p>

              <NoAxisWarning tons={productSearchParams?.tons} className="mb-2" />

              <div className="px-1">
                <RadioList
                  name="axis"
                  list={axisOptionData}
                  value={productSearchParams?.axis?.code || ''}
                  onChange={(val) => {
                    onChangeRadioInput(val, 'axis');
                  }}
                  isDisabled={isUnderFourTons(productSearchParams?.tons)}
                ></RadioList>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DualFooterButton
        leftButtonText="초기화"
        onClickLeftButton={() => {
          clear();
        }}
        rightButtonText="검색"
        onClickRightButton={() => {
          onClickSearch();
          setPriceProductSearchParams(productSearchParams);
        }}
        disabledRight={isDisabledSearchBtn()}
      ></DualFooterButton>
      <BottomSheetPopup
        isOpen={isShow}
        onClose={() => {
          setIsShow(false);
          // 마력수 selectbox가 닫힐 때 power가 없으면 에러 표시
          if (title === 'power' && !productSearchParams?.power) {
            const isDisabled =
              !productSearchParams?.manufacturerCategories?.name ||
              !productSearchParams?.model?.name ||
              !productSearchParams?.tons;
            if (!isDisabled) {
              setPowerError(true);
              setPowerErrorMsg('마력을 선택해주세요.');
            }
          }
        }}
        title={keyValueList[title] || '마력수'}
      >
        <div className="px-4 pb-8">
          {isLoadingHorsepower ? (
            <div className="py-8 text-center text-gray-6">로딩 중...</div>
          ) : (
            <RadioList
              name={title}
              list={title === 'power' ? horsepowerOptions : optionData}
              value={title === 'power' ? productSearchParams?.power || '' : getValue(title) || ''}
              onChange={(val) => {
                if (title === 'power') {
                  const selectedPower = val.code;
                  setProductSearchParams({ ...productSearchParams, power: selectedPower });

                  // 선택한 경우는 에러 없음
                  setPowerError(false);
                  setPowerErrorMsg('');
                  setTimeout(() => {
                    setIsShow(false);
                  }, 200);
                } else {
                  onClickItem(val, title);
                }
              }}
              horizontal={false}
            ></RadioList>
          )}
        </div>
      </BottomSheetPopup>
    </>
  );
};

export default PriceTrendSearchForm;
